/**
 * The short's second question (pulled-blanks v2 D17, §2.5 door 1). The puller found FEWER on the shelf
 * than the list said — the first place a shelf discrepancy is ever discovered — so this asks the one thing
 * they can answer right there: *how many are left on the shelf?* The answer is a SHELF-BASIS COUNT for this
 * one blank (`POST /inventory/:ssSku/count` with `{basis:'shelf', shelfQty, asOf, pulledSeen}`), judged by
 * the server against the ledger as it stands (§2.8): book = left + Σ out over the recorded sheets, so the
 * phantom blanks leave the book now and Select in stock / the reorder see the truth before the next sheet
 * is built.
 *
 * What it posts is ONLY the figure the puller typed plus advisory ids. `asOf` and `pulledSeen` come from
 * the drawer read (`GET /inventory/:ssSku`) made AFTER the short landed on the sheet. ⚠️ That read lists
 * the open sheets from an eventually-consistent index, so it can still MISS this sheet's fresh record (or
 * carry an older revision of it) — and the server's `pulled_rose` guard cannot catch the omission, because
 * the row's `at` is before `asOf`. The dialog therefore seeds `pulledSeen` with THIS sheet from the tick's
 * own response (batchId, the revision it landed at, the quantity got) whenever the read omits it or is
 * behind: a seen sheet is re-read strongly consistent server-side, so the record is counted — and a true
 * mismatch is refused as `pulled_changed`, fail-safe. Every refusal (moved_since_load / pulled_rose /
 * pulled_changed / ledger_moving) writes nothing and is shown in the server's words; the short itself stays
 * recorded on the sheet either way — *I'll look later* is honest.
 *
 * Gate-aware: a shelf count keeps the inventory-write gate (`admin` / `inventory-admin`), unlike the pull
 * record (any dashboard sign-in). A login without it is told so and asked to tell the office; nothing is
 * posted. The role flag is cosmetic — the server's 403 is the real gate and renders the same copy.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { isImplausible, parseCount } from './count-shelf-lib';
import { invalidateInventory } from './inventory-lib';

type BlankDetail = components['schemas']['BlankInventoryDetail'];

export type PullShortTarget = {
  ssSku: string;
  /** "Tultex 602" */
  label: string;
  /** "Black · XL" */
  variant: string;
  listed: number;
  got: number;
  /** "Batch #219" — names the sheet in the count's reason. */
  sheetLabel: string;
  /** The sheet the short was recorded on, and the record revision the tick landed at. */
  batchId: string;
  rev: number;
};

type PulledSheets = NonNullable<BlankDetail['pulled']>['sheets'];

/**
 * The recorded sheets the count will be judged against: the drawer read's, with THIS sheet taken from the
 * tick's own response when the read omits it or carries an older revision (the index can lag the header
 * write by a moment). Pure, so the hazard is pinned by a test.
 */
export function seenSheetsForShort(
  sheets: PulledSheets,
  target: Pick<PullShortTarget, 'batchId' | 'rev' | 'got'>,
): PulledSheets {
  const own = sheets.find((s) => s.batchId === target.batchId);
  if (own && own.rev >= target.rev) return sheets;
  const fresh = {
    batchId: target.batchId,
    seq: own?.seq,
    status: own?.status ?? 'READY',
    recorded: target.got,
    drawn: own?.drawn ?? 0,
    out: Math.max(0, target.got - (own?.drawn ?? 0)),
    rev: target.rev,
    at: own?.at ?? new Date(0).toISOString(),
    via: own?.via ?? 'pull-list',
    who: own?.who,
  };
  return own ? sheets.map((s) => (s.batchId === target.batchId ? fresh : s)) : [...sheets, fresh];
}

/** The size alone ("XL") from a "Black · XL" variant, for the question's wording. */
function sizeOf(variant: string): string {
  const parts = variant.split(' · ');
  return parts.length > 1 ? (parts.at(-1) ?? '') : '';
}

/** "#219 20 · #220 20 · #221 7" — the recorded sheets the book adds back, as the count will see them. */
function sheetsLine(sheets: PulledSheets, own: { batchId: string; sheetLabel: string }): string {
  return sheets
    .filter((s) => s.out > 0)
    .map(
      (s) =>
        `${s.seq ? `#${s.seq}` : s.batchId === own.batchId ? own.sheetLabel.replace(/^Batch /, '') : s.batchId.slice(0, 8)} ${s.out}`,
    )
    .join(' · ');
}

export function PullShortShelfDialog({
  target,
  onClose,
}: {
  target: PullShortTarget;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const short = target.listed - target.got;
  const size = sizeOf(target.variant);

  // The drawer's read, AFTER the short landed: the book, and the pulled block with `asOf` + the recorded
  // sheets (this one included). Fresh every open — a stale block would carry a stale `asOf`.
  const detail = useQuery({
    queryKey: ['inventory', target.ssSku],
    staleTime: 0,
    enabled: canWrite,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/{ssSku}', {
        params: { path: { ssSku: target.ssSku } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load this blank.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const [left, setLeft] = useState('0');
  const [confirming, setConfirming] = useState(false);
  /** One idempotency token per FIGURE: a corrected number is a new observation, never a replay. */
  const tokens = useRef(new Map<number, string>());
  const [outcome, setOutcome] = useState<
    | { kind: 'saved'; onHand: number; shelfQty: number; pulledQty: number }
    | { kind: 'gated' }
    | { kind: 'refused'; title: string; detail: string }
    | null
  >(null);

  const n = parseCount(left);
  const pulled = detail.data?.pulled;
  const onHand = detail.data?.onHand.onHand;
  // The sheets the count is judged against — this sheet's fresh record included (see the header).
  const seen = pulled ? seenSheetsForShort(pulled.sheets, target) : [];
  const now = seen.reduce((s, x) => s + x.out, 0);
  const book = n == null ? null : n + now;
  const delta = book != null && onHand != null ? book - onHand : null;

  const write = useMutation({
    mutationFn: async (shelfQty: number) => {
      if (!pulled)
        throw new Error('The pulled stacks could not be read — count this blank at /count.');
      let token = tokens.current.get(shelfQty);
      if (!token) {
        token = crypto.randomUUID();
        tokens.current.set(shelfQty, token);
      }
      const res = await client.POST('/inventory/{ssSku}/count', {
        params: { path: { ssSku: target.ssSku } },
        // Only the typed figure plus advisory ids: `asOf` and the seen sheets are what the row loaded with.
        body: {
          basis: 'shelf',
          shelfQty,
          asOf: pulled.asOf,
          pulledSeen: seen.map((s) => ({ batchId: s.batchId, rev: s.rev, qty: s.out })),
          reason: `short at the pull · ${target.sheetLabel}`,
          clientToken: token,
        },
      });
      if (res.error) {
        if (res.response?.status === 403) return { kind: 'gated' as const };
        return {
          kind: 'refused' as const,
          title: res.error.error.title ?? 'Not recorded',
          detail: res.error.error.detail ?? 'The count was refused — nothing was written.',
        };
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const d = res.data.data;
      return {
        kind: 'saved' as const,
        onHand: d.onHand,
        shelfQty: d.movement?.shelfQty ?? shelfQty,
        pulledQty: d.movement?.pulledQty ?? now,
      };
    },
    onSuccess: (out) => {
      setOutcome(out);
      if (out.kind === 'saved') invalidateInventory(qc);
    },
  });

  const submit = () => {
    if (n == null || onHand == null) return;
    // The same keying-slip + emptied-bin confirm as `/count` — on the BOOK the count would anchor.
    if (!confirming && book != null && isImplausible(book, onHand)) {
      setConfirming(true);
      return;
    }
    write.mutate(n);
  };

  const name = `${target.label} · ${target.variant}`;

  return (
    <Dialog open onOpenChange={(v) => !v && !write.isPending && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{short} short — fix the book?</DialogTitle>
          <DialogDescription>
            {name} · listed {target.listed}, got {target.got} for {target.sheetLabel}.
          </DialogDescription>
        </DialogHeader>

        {!canWrite && (
          <Alert>
            <AlertTitle>
              Recorded {target.got} of {target.listed}.
            </AlertTitle>
            <AlertDescription>
              This login can't count stock — tell the office to count this blank. The short is on
              the sheet.
            </AlertDescription>
          </Alert>
        )}

        {canWrite && detail.isPending && (
          <div className="space-y-2">
            <Skeleton className="h-24 w-full" />
            <p className="text-xs text-muted-foreground">Reading the book for this blank…</p>
          </div>
        )}
        {canWrite && detail.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {detail.error instanceof Error ? detail.error.message : 'Failed to load this blank.'}{' '}
              The short is recorded — count this blank at /count.
            </AlertDescription>
          </Alert>
        )}
        {canWrite && detail.data && !pulled && (
          <Alert>
            <AlertDescription>
              The server couldn't derive the pulled stacks for this blank — the short is recorded;
              count it at /count.
            </AlertDescription>
          </Alert>
        )}

        {canWrite && pulled && onHand != null && !outcome && (
          <div className="space-y-3">
            <p className="text-sm">
              The book expected{' '}
              <b className="tabular-nums">{Math.max(0, onHand - (now - target.got))}</b>
              {size ? ` ${size}` : ''} on the shelf; you found{' '}
              <b className="tabular-nums">{target.got}</b>. Any more anywhere? If not, how many
              {size ? ` ${size}` : ''} are <b>left on the shelf</b> now?
            </p>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="pull-short-left" className="text-xs">
                  Left on the shelf
                </Label>
                <Input
                  id="pull-short-left"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="h-11 w-24 text-lg tabular-nums"
                  value={left}
                  onChange={(e) => {
                    setLeft(e.target.value);
                    setConfirming(false);
                  }}
                  aria-invalid={left.trim() !== '' && n == null ? true : undefined}
                />
              </div>
              <p className="pb-2 text-xs text-muted-foreground">
                Writes a shelf count for this blank only:{' '}
                <span className="tabular-nums">{n ?? '…'}</span> left +{' '}
                <span className="tabular-nums">{now}</span> pulled
                {seen.some((s) => s.out > 0) ? ` (${sheetsLine(seen, target)})` : ''} →{' '}
                <b className="tabular-nums">{book ?? '…'}</b> on the books
                {delta != null && (
                  <span className="tabular-nums">
                    {' '}
                    ({delta > 0 ? '+' : ''}
                    {delta})
                  </span>
                )}
                .
              </p>
            </div>
            {confirming && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                <AlertTitle>That is a big change</AlertTitle>
                <AlertDescription>
                  The book goes from {onHand} to {book}. Record it anyway?
                </AlertDescription>
              </Alert>
            )}
            {write.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {write.error instanceof Error ? write.error.message : 'Count failed.'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {outcome?.kind === 'saved' && (
          <Alert>
            <AlertTitle>Recorded</AlertTitle>
            <AlertDescription>
              {outcome.shelfQty} on the shelf + {outcome.pulledQty} pulled → {outcome.onHand} on the
              books. Select in stock and the reorder see it now.
            </AlertDescription>
          </Alert>
        )}
        {outcome?.kind === 'gated' && (
          <Alert>
            <AlertTitle>
              Recorded {target.got} of {target.listed}.
            </AlertTitle>
            <AlertDescription>
              This login can't count stock — tell the office to count this blank. Nothing was
              written to the book.
            </AlertDescription>
          </Alert>
        )}
        {outcome?.kind === 'refused' && (
          <Alert variant="destructive">
            <AlertTitle>{outcome.title}</AlertTitle>
            <AlertDescription>
              {outcome.detail} Nothing was written; the short stays on the sheet — count this blank
              at /count.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {canWrite && detail.isPending ? (
            <Button variant="outline" onClick={onClose}>
              I'll look later
            </Button>
          ) : outcome || !canWrite || !pulled || onHand == null ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={write.isPending}>
                I'll look later
              </Button>
              <Button onClick={submit} disabled={n == null || write.isPending}>
                {write.isPending && <Loader2 className="size-4 animate-spin" />}
                {confirming ? 'Yes, record it' : `Record: ${n ?? '…'} left on the shelf`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
